import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { apiGet, apiPost, apiPostForm, APIError } from "@/api/client";
import {
  proxyProfilePath,
  proxyProfileProbePath,
  proxyProfilesImportPath,
  proxyProfilesQueryPath,
  proxyProfilesPath,
} from "@/api/endpoints";
import type {
  ProxyProfileEntry,
  ProxyProfileProbeResponse,
  ProxyProfilesImportResponse,
  ProxyProfileSaveResponse,
  ProxyProfilesResponse,
} from "@/api/types";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { QueryView } from "@/components/ui/QueryView";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { Textarea } from "@/components/ui/Textarea";
import { toast } from "@/components/ui/Sonner";
import { formatMs } from "@/lib/format";
import { refreshProxyQueries } from "@/lib/proxyFlowQueries";
import { parseProxyProfilesImportBody } from "@/lib/proxyProfilesImport";

type ProxyProfileFormState = {
  id: number;
  profile_key: string;
  display_name: string;
  protocol: string;
  server: string;
  server_port: string;
  username: string;
  password: string;
  tags: string;
  state: string;
};

type ProfileStateFilter = "all" | "enabled" | "disabled" | "maintenance";
type ProfileHealthFilter = "all" | "ok" | "failed" | "unchecked";

function emptyProxyProfileForm(): ProxyProfileFormState {
  return {
    id: 0,
    profile_key: "",
    display_name: "",
    protocol: "socks",
    server: "",
    server_port: "1080",
    username: "",
    password: "",
    tags: "",
    state: "enabled",
  };
}

function proxyProfileToForm(profile: ProxyProfileEntry): ProxyProfileFormState {
  return {
    id: profile.id,
    profile_key: profile.profile_key,
    display_name: profile.display_name,
    protocol: profile.protocol || "socks",
    server: profile.server,
    server_port: String(profile.server_port || ""),
    username: profile.username,
    password: "",
    tags: profile.tags,
    state: profile.state || "enabled",
  };
}

function apiErrorMessage(err: unknown): string {
  return err instanceof APIError
    ? err.message
    : err instanceof Error
      ? err.message
      : String(err);
}

function profileProbeHealth(profile: ProxyProfileEntry): ProfileHealthFilter {
  if (profile.last_probe_at === 0) {
    return "unchecked";
  }
  return profile.last_probe_ok ? "ok" : "failed";
}

function profileMatchesSearch(profile: ProxyProfileEntry, rawSearch: string) {
  const search = rawSearch.trim().toLowerCase();
  if (!search) {
    return true;
  }
  return [
    profile.profile_key,
    profile.display_name,
    profile.protocol,
    profile.server,
    String(profile.server_port),
    `${profile.server}:${profile.server_port}`,
    profile.probe_exit_ip,
    profile.tags,
    profile.state,
    profile.last_error,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(search);
}

function useDebouncedValue(value: string, delayMs: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

export function ProxyProfilesManager({ canWrite }: { canWrite: boolean }) {
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] =
    useState<ProfileStateFilter>("all");
  const [healthFilter, setHealthFilter] =
    useState<ProfileHealthFilter>("all");
  const debouncedSearch = useDebouncedValue(search, 250);
  const profilesQuery = useQuery({
    queryKey: [
      "proxy-profiles",
      { search: debouncedSearch, stateFilter, healthFilter },
    ],
    queryFn: () =>
      apiGet<ProxyProfilesResponse>(
        proxyProfilesQueryPath({
          q: debouncedSearch,
          state: stateFilter,
          health: healthFilter,
        }),
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <QueryView query={profilesQuery}>
      {(profilesData) => (
        <ProxyProfilesPanel
          profiles={profilesData.items}
          count={profilesData.count}
          canWrite={canWrite}
          search={search}
          stateFilter={stateFilter}
          healthFilter={healthFilter}
          onSearchChange={setSearch}
          onStateFilterChange={setStateFilter}
          onHealthFilterChange={setHealthFilter}
        />
      )}
    </QueryView>
  );
}

function ProxyProfilesPanel({
  profiles,
  count,
  canWrite,
  search,
  stateFilter,
  healthFilter,
  onSearchChange,
  onStateFilterChange,
  onHealthFilterChange,
}: {
  profiles: ProxyProfileEntry[];
  count: number;
  canWrite: boolean;
  search: string;
  stateFilter: ProfileStateFilter;
  healthFilter: ProfileHealthFilter;
  onSearchChange: (next: string) => void;
  onStateFilterChange: (next: ProfileStateFilter) => void;
  onHealthFilterChange: (next: ProfileHealthFilter) => void;
}) {
  const [editing, setEditing] = useState<ProxyProfileFormState>(
    emptyProxyProfileForm(),
  );
  const [saving, setSaving] = useState(false);
  const [importBody, setImportBody] = useState("");
  const [importing, setImporting] = useState(false);
  const [probingID, setProbingID] = useState<number | null>(null);
  const [updatingStateID, setUpdatingStateID] = useState<number | null>(null);
  const queryClient = useQueryClient();

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    try {
      const path = editing.id ? proxyProfilePath(editing.id) : proxyProfilesPath;
      await apiPostForm<ProxyProfileSaveResponse>(path, {
        profile_key: editing.profile_key,
        display_name: editing.display_name,
        protocol: editing.protocol,
        server: editing.server,
        server_port: editing.server_port,
        username: editing.username,
        password: editing.password,
        tags: editing.tags,
        state: editing.state,
      });
      toast.success(editing.id ? "已保存代理节点" : "已创建代理节点", {
        description: "daemon 会自动分配未绑定槽位并异步下发配置",
      });
      setEditing(emptyProxyProfileForm());
      refreshProxyQueries(queryClient, undefined, {
        includeProfiles: true,
        delayed: true,
      });
    } catch (err) {
      toast.error("保存代理节点失败", { description: apiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  }

  async function importProfiles(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setImporting(true);
    try {
      const parsed = parseProxyProfilesImportBody(importBody);
      const result = await apiPost<ProxyProfilesImportResponse>(
        proxyProfilesImportPath,
        parsed,
      );
      toast.success(`已导入 ${result.count} 个代理节点`, {
        description:
          result.affected_agents.length > 0
            ? `已触发 ${result.affected_agents.length} 台 agent 自动分配`
            : "当前没有需要自动分配的 agent",
      });
      setImportBody("");
      refreshProxyQueries(queryClient, undefined, {
        includeProfiles: true,
        delayed: true,
      });
    } catch (err) {
      toast.error("导入代理池失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setImporting(false);
    }
  }

  async function probeProfile(profile: ProxyProfileEntry) {
    setProbingID(profile.id);
    try {
      const response = await apiPostForm<ProxyProfileProbeResponse>(
        proxyProfileProbePath(profile.id),
        {},
      );
      toast.success("代理节点可用", {
        description: [
          response.result.exit_ip ? `出口 ${response.result.exit_ip}` : "",
          `${response.result.latency_ms} ms`,
          response.affected_agents?.length
            ? `已触发 ${response.affected_agents.length} 台 agent 分发`
            : "",
        ]
          .filter(Boolean)
          .join(" / "),
      });
      refreshProxyQueries(queryClient, undefined, {
        includeProfiles: true,
        delayed: true,
      });
    } catch (err) {
      toast.error("代理节点探测失败", {
        description: apiErrorMessage(err),
      });
      refreshProxyQueries(queryClient, undefined, { includeProfiles: true });
    } finally {
      setProbingID(null);
    }
  }

  async function updateProfileState(profile: ProxyProfileEntry, state: string) {
    if (state === profile.state) {
      return;
    }
    setUpdatingStateID(profile.id);
    try {
      await apiPostForm<ProxyProfileSaveResponse>(proxyProfilePath(profile.id), {
        profile_key: profile.profile_key,
        display_name: profile.display_name,
        protocol: profile.protocol,
        server: profile.server,
        server_port: String(profile.server_port),
        username: profile.username,
        password: "",
        tags: profile.tags,
        state,
      });
      toast.success("已更新代理节点状态", {
        description:
          state === "enabled"
            ? "daemon 会重新纳入自动分配和 sing-box 配置生成"
            : "已绑定槽位会走清理配置，启动入口也会按新状态拦截",
      });
      refreshProxyQueries(queryClient, undefined, {
        includeProfiles: true,
        delayed: true,
      });
    } catch (err) {
      toast.error("更新代理节点状态失败", {
        description: apiErrorMessage(err),
      });
    } finally {
      setUpdatingStateID(null);
    }
  }

  const enabledCount = profiles.filter((profile) => profile.state === "enabled")
    .length;
  const failedProbeCount = profiles.filter(
    (profile) => profile.last_probe_at > 0 && !profile.last_probe_ok,
  ).length;
  const visibleProfiles = profiles.filter((profile) => {
    if (stateFilter !== "all" && profile.state !== stateFilter) {
      return false;
    }
    if (
      healthFilter !== "all" &&
      profileProbeHealth(profile) !== healthFilter
    ) {
      return false;
    }
    return profileMatchesSearch(profile, search);
  });
  const hasActiveFilter =
    search.trim() !== "" || stateFilter !== "all" || healthFilter !== "all";

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <PoolSummaryItem label="代理节点" value={count} />
        <PoolSummaryItem label="启用节点" value={enabledCount} />
        <PoolSummaryItem label="探测失败" value={failedProbeCount} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>上游代理池</CardTitle>
          <CardDescription>
            daemon 级资源，供所有 agent 的启动槽位自动分配和 sing-box 配置生成。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-3 overflow-x-auto">
              <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_160px_160px_auto]">
                <Input
                  value={search}
                  onChange={(event) => onSearchChange(event.target.value)}
                  aria-label="搜索代理池"
                  className="h-8 text-xs"
                  placeholder="搜索 profile_key / 地址 / 标签 / 出口 IP"
                />
                <Select
                  value={stateFilter}
                  onValueChange={(value) =>
                    onStateFilterChange(value as ProfileStateFilter)
                  }
                >
                  <SelectTrigger aria-label="状态过滤" className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部状态</SelectItem>
                    <SelectItem value="enabled">enabled</SelectItem>
                    <SelectItem value="disabled">disabled</SelectItem>
                    <SelectItem value="maintenance">maintenance</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={healthFilter}
                  onValueChange={(value) =>
                    onHealthFilterChange(value as ProfileHealthFilter)
                  }
                >
                  <SelectTrigger aria-label="探测过滤" className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部探测</SelectItem>
                    <SelectItem value="ok">可用</SelectItem>
                    <SelectItem value="failed">失败</SelectItem>
                    <SelectItem value="unchecked">未检测</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!hasActiveFilter}
                  onClick={() => {
                    onSearchChange("");
                    onStateFilterChange("all");
                    onHealthFilterChange("all");
                  }}
                >
                  重置
                </Button>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">
                显示 {visibleProfiles.length} / {profiles.length} 个代理节点
              </div>
              <Table dense>
                <TableHeader>
                  <TableRow>
                    <TableHead>节点</TableHead>
                    <TableHead>协议</TableHead>
                    <TableHead>地址</TableHead>
                    <TableHead>出口 IP</TableHead>
                    <TableHead>最近探测</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleProfiles.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-xs text-[hsl(var(--muted-foreground))]"
                      >
                        {profiles.length === 0
                          ? "暂无代理节点"
                          : "当前筛选没有代理节点"}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {visibleProfiles.map((profile) => (
                    <TableRow key={profile.id}>
                      <TableCell>
                        <div className="font-mono">{profile.profile_key}</div>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
                          {profile.display_name || profile.tags}
                        </div>
                      </TableCell>
                      <TableCell>{profile.protocol}</TableCell>
                      <TableCell className="font-mono">
                        {profile.server}:{profile.server_port}
                      </TableCell>
                      <TableCell className="font-mono">
                        {profile.probe_exit_ip || ""}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <StatusBadge
                              variant={
                                profile.last_probe_at === 0
                                  ? "neutral"
                                  : profile.last_probe_ok
                                    ? "ok"
                                    : "danger"
                              }
                            >
                              {profile.last_probe_at === 0
                                ? "未检测"
                                : profile.last_probe_ok
                                  ? "可用"
                                  : "失败"}
                            </StatusBadge>
                            <span className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                              {profile.last_probe_latency_ms
                                ? `${profile.last_probe_latency_ms}ms`
                                : ""}
                            </span>
                          </div>
                          <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                            {profile.probe_exit_ip || profile.last_error || ""}
                          </div>
                          <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
                            {profile.last_probe_at
                              ? formatMs(profile.last_probe_at)
                              : ""}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={profile.state || "enabled"}
                          onValueChange={(value) =>
                            void updateProfileState(profile, value)
                          }
                          disabled={!canWrite || updatingStateID === profile.id}
                        >
                          <SelectTrigger
                            aria-label={`${profile.profile_key} 状态`}
                            className="h-7 min-w-32 text-[10px]"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="enabled">enabled</SelectItem>
                            <SelectItem value="disabled">disabled</SelectItem>
                            <SelectItem value="maintenance">
                              maintenance
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!canWrite || probingID === profile.id}
                            onClick={() => void probeProfile(profile)}
                          >
                            {probingID === profile.id ? "检测中" : "检测"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!canWrite}
                            onClick={() => setEditing(proxyProfileToForm(profile))}
                          >
                            编辑
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <ProfileEditForm
              editing={editing}
              saving={saving}
              canWrite={canWrite}
              onChange={setEditing}
              onSubmit={save}
              onReset={() => setEditing(emptyProxyProfileForm())}
            />
            <form
              className="space-y-3 rounded border border-[hsl(var(--border))] p-3 text-xs lg:col-start-2"
              onSubmit={importProfiles}
            >
              <div className="font-medium">批量导入</div>
              <Textarea
                value={importBody}
                onChange={(e) => setImportBody(e.target.value)}
                disabled={!canWrite || importing}
                className="min-h-36 font-mono text-xs"
                placeholder={
                  "socks5://user:pass@host:1080?profile_key=proxy-01\nhttp://host:8080\nhost:1081:user:pass"
                }
              />
              <Button
                type="submit"
                size="sm"
                disabled={!canWrite || importing || importBody.trim() === ""}
                className="w-full"
              >
                导入代理池
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PoolSummaryItem({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          {label}
        </div>
        <div className="mt-1 font-mono text-2xl">{value}</div>
      </CardContent>
    </Card>
  );
}

function ProfileEditForm({
  editing,
  saving,
  canWrite,
  onChange,
  onSubmit,
  onReset,
}: {
  editing: ProxyProfileFormState;
  saving: boolean;
  canWrite: boolean;
  onChange: (next: ProxyProfileFormState) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onReset: () => void;
}) {
  return (
    <form
      className="space-y-3 rounded border border-[hsl(var(--border))] p-3 text-xs"
      onSubmit={onSubmit}
    >
      <div className="flex items-center justify-between">
        <div className="font-medium">
          {editing.id ? "编辑代理节点" : "新增代理节点"}
        </div>
        {editing.id ? (
          <Button type="button" size="sm" variant="ghost" onClick={onReset}>
            新建
          </Button>
        ) : null}
      </div>
      <label className="block space-y-1">
        <span>profile_key</span>
        <Input
          value={editing.profile_key}
          onChange={(e) =>
            onChange({ ...editing, profile_key: e.target.value })
          }
          disabled={!canWrite || saving || editing.id > 0}
          required
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block space-y-1">
          <span>协议</span>
          <Select
            value={editing.protocol}
            onValueChange={(value) => onChange({ ...editing, protocol: value })}
            disabled={!canWrite || saving}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="socks">socks</SelectItem>
              <SelectItem value="http">http</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="block space-y-1">
          <span>状态</span>
          <Select
            value={editing.state}
            onValueChange={(value) => onChange({ ...editing, state: value })}
            disabled={!canWrite || saving}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="enabled">enabled</SelectItem>
              <SelectItem value="disabled">disabled</SelectItem>
              <SelectItem value="maintenance">maintenance</SelectItem>
            </SelectContent>
          </Select>
        </label>
      </div>
      <label className="block space-y-1">
        <span>server</span>
        <Input
          value={editing.server}
          onChange={(e) => onChange({ ...editing, server: e.target.value })}
          disabled={!canWrite || saving}
          required
        />
      </label>
      <label className="block space-y-1">
        <span>server_port</span>
        <Input
          value={editing.server_port}
          onChange={(e) =>
            onChange({ ...editing, server_port: e.target.value })
          }
          disabled={!canWrite || saving}
          required
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block space-y-1">
          <span>username</span>
          <Input
            value={editing.username}
            onChange={(e) => onChange({ ...editing, username: e.target.value })}
            disabled={!canWrite || saving}
          />
        </label>
        <label className="block space-y-1">
          <span>password</span>
          <Input
            value={editing.password}
            type="password"
            placeholder={editing.id ? "留空沿用旧密码" : ""}
            onChange={(e) => onChange({ ...editing, password: e.target.value })}
            disabled={!canWrite || saving}
          />
        </label>
      </div>
      <label className="block space-y-1">
        <span>display_name</span>
        <Input
          value={editing.display_name}
          onChange={(e) =>
            onChange({ ...editing, display_name: e.target.value })
          }
          disabled={!canWrite || saving}
        />
      </label>
      <label className="block space-y-1">
        <span>tags</span>
        <Textarea
          value={editing.tags}
          onChange={(e) => onChange({ ...editing, tags: e.target.value })}
          disabled={!canWrite || saving}
          className="min-h-14 font-mono text-xs"
        />
      </label>
      <Button
        type="submit"
        size="sm"
        disabled={!canWrite || saving}
        className="w-full"
      >
        保存代理节点
      </Button>
    </form>
  );
}
